import { type FC, useState, useRef } from "react";
import { usePouch, useView } from "use-pouchdb";
import * as uuid from "uuid";

import { isIconDoc } from "../../common/schema";
import { assertIsExistingIconDoc } from "../existing_docs";
import { IconImageFromDoc } from "../images";
import { generateThumbnail } from "../thumbnails";

export const IconLibraryPage: FC = () => {
  const db = usePouch();
  const { rows } = useView("built/icons", { include_docs: true });
  const [search, setSearch] = useState("");
  const [pending, setPending] = useState<{file: File; previewUrl: string; name: string} | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const filtered = rows.filter(row => {
    if (!isIconDoc(row.doc)) return false;
    return search === "" || row.doc.name.toLowerCase().includes(search.toLowerCase());
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file == null || !file.type.startsWith("image/")) return;
    setPending({ file, previewUrl: URL.createObjectURL(file), name: "" });
    e.target.value = "";
  };

  const savePendingIcon = async () => {
    if (pending == null) return;
    const thumbBlob = await generateThumbnail(pending.previewUrl, 200);
    const iconDoc = {
      _id: `icon:${uuid.v7()}`,
      name: pending.name,
      deleted_at: null,
      _attachments: {
        file: { content_type: pending.file.type, data: pending.file },
        ...(thumbBlob != null ? { thumbnail: { content_type: "image/jpeg", data: thumbBlob } } : {}),
      },
    };
    db.put(iconDoc).then(() => {
      URL.revokeObjectURL(pending.previewUrl);
      setPending(null);
    }).catch(console.error);
  };

  const cancelPending = () => {
    if (pending != null) URL.revokeObjectURL(pending.previewUrl);
    setPending(null);
  };

  return <div style={{padding: "6px"}}>
    <h3 style={{margin: "0 0 8px"}}>Icon library</h3>
    <div style={{display: "flex", gap: "8px", marginBottom: "8px", alignItems: "center"}}>
      <input
        type="search"
        placeholder="Search icons…"
        value={search}
        onChange={e => setSearch(e.target.value)}
        data-testid="icon-search"
        style={{flexGrow: 1}}
      />
      <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileChange} style={{display: "none"}} />
      <button type="button" onClick={() => fileInputRef.current?.click()}>Add icon</button>
    </div>

    {pending != null && (
      <div data-testid="pending-icon-row" style={{border: "1px dashed #aaa", padding: "8px", marginBottom: "8px", display: "flex", gap: "8px", alignItems: "flex-start"}}>
        <img src={pending.previewUrl} style={{width: "80px", height: "80px", objectFit: "contain", backgroundColor: "#ddd", flexShrink: 0}} />
        <div style={{display: "flex", flexDirection: "column", gap: "4px", flexGrow: 1}}>
          <input
            type="text"
            value={pending.name}
            onChange={e => setPending({...pending, name: e.target.value})}
            placeholder="Icon name (e.g. resistor, capacitor, USB cable)"
            data-testid="pending-icon-name"
            autoFocus
            style={{width: "100%", boxSizing: "border-box"}}
          />
          <div style={{display: "flex", gap: "4px"}}>
            <button type="button" onClick={() => void savePendingIcon()} disabled={pending.name.trim() === ""} data-testid="save-icon-button">Save</button>
            <button type="button" onClick={cancelPending}>Cancel</button>
          </div>
        </div>
      </div>
    )}

    {filtered.length === 0 && pending == null && (
      <p style={{color: "#888", fontSize: "small"}}>No icons yet. Click "Add icon" to upload one.</p>
    )}

    <div style={{display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(100px, 1fr))", gap: "8px"}}>
      {filtered.map(row => {
        if (!isIconDoc(row.doc)) return null;
        assertIsExistingIconDoc(row.doc);
        return <div key={row.doc._id} data-testid="icon-card" style={{border: "1px solid #ddd", borderRadius: "4px", padding: "4px", display: "flex", flexDirection: "column", gap: "4px", alignItems: "center"}}>
          <IconImageFromDoc doc={row.doc} style={{width: "80px", height: "80px"}} />
          <span style={{fontSize: "small", textAlign: "center", wordBreak: "break-word"}}>{row.doc.name}</span>
          <button type="button" onClick={() => db.put({...row.doc, deleted_at: new Date().toISOString()}).catch(console.error)} style={{fontSize: "small"}}>Delete</button>
        </div>;
      })}
    </div>
  </div>;
};
